// The two overlays a long press on a photo opens, in the order they open.
//
// W39.4 put a confirm between 「刪除這張」 and the delete, because a soft delete
// with no restore route is otherwise one tap from gone forever. W39.53 removed
// 「設為封面」 from this sheet after finding it was a `console.warn` and nothing
// else — and left a note saying to put it back once the server could hold a
// chosen cover. W39.54 is that: `AlbumUpdate` grew `cover_media_id` +
// `cover_is_manual` (migration `x9y0z1a2b3c4`), so the action is live again and
// lives here with the others rather than in the route.
//
// Extracted from `routes/AlbumDetail.tsx` to stay under the 15 KB route ceiling
// (AGENTS.md §3 #10). It is presentational on purpose: the route owns which
// item is pending and what to do about it, so this file has no fetch and no
// state of its own beyond what the route passes in.

import { Share2, Image as ImageIcon } from "lucide-react";

import { ActionSheet } from "@/components/system";
import { ConfirmDialog } from "@/components/patterns/ConfirmDialog";
import type { AlbumItemRead } from "@/lib/api/album";

export interface AlbumPhotoActionsProps {
  /** The photo the long press opened the sheet for, or null. */
  item: AlbumItemRead | null;
  /**
   * What the sheet says it is acting on. A photo with no caption used to open
   * the sheet with a blank header — the title was `caption ?? ""` — so the three
   * actions floated with no subject; the route now falls back to 「第 N 張照片」.
   */
  title: string;
  /** Set while the confirm dialog is up. */
  pendingDelete: AlbumItemRead | null;
  onCloseSheet: () => void;
  onDismissConfirm: () => void;
  onRequestDelete: (item: AlbumItemRead) => void;
  onShare: () => void;
  onSetCover: (item: AlbumItemRead) => void;
  onConfirmDelete: () => void;
}

export function AlbumPhotoActions({
  item,
  title,
  pendingDelete,
  onCloseSheet,
  onDismissConfirm,
  onRequestDelete,
  onShare,
  onSetCover,
  onConfirmDelete,
}: AlbumPhotoActionsProps) {
  return (
    <>
      <ActionSheet
        open={item !== null}
        onClose={onCloseSheet}
        title={title}
        actions={
          item
            ? [
                {
                  id: "delete-photo",
                  label: "刪除這張",
                  tone: "danger" as const,
                  onClick: async () => {
                    onCloseSheet();
                    onRequestDelete(item);
                  },
                },
                {
                  id: "share-photo",
                  label: "分享這張",
                  leadingIcon: <Share2 size={18} aria-hidden />,
                  onClick: () => {
                    onCloseSheet();
                    onShare();
                  },
                },
                {
                  id: "set-cover",
                  label: "設為封面",
                  leadingIcon: <ImageIcon size={18} aria-hidden />,
                  onClick: () => {
                    onCloseSheet();
                    onSetCover(item);
                  },
                },
              ]
            : []
        }
        testId="album-photo-action-sheet"
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) onDismissConfirm();
        }}
        title="從相簿移除這張照片？"
        // W56.0 J (ADR-0201) — the copy was corrected, because it promised a
        // recovery route that does not exist.
        //
        // It used to say 「日後仍可從「未分類」再加入其他相簿」. There is no
        // such route: the album module has `DELETE /albums/{id}/items/{item}`
        // and **no** endpoint that puts an existing media item back into an
        // album (`AlbumItem` rows are only created by the upload flow), and no
        // listing of media that lost its album link. So the old sentence
        // described a way back that the product does not have — the same
        // false-statement class ADR-0079/0080 set out to remove.
        //
        // The confirmation therefore **stays** here, which is the spec's own
        // rule applied to the facts: 「不可逆操作保留確認框」. A soft delete
        // with a working restore gets the undo bar instead; this one has no
        // restore, so it keeps the box.
        description="照片會從這個相簿移除。照片本身仍保留在伺服器上，但這個 app 目前沒有重新加入的入口。"
        confirmLabel="從相簿移除"
        cancelLabel="保留"
        destructive
        onConfirm={onConfirmDelete}
      />
    </>
  );
}
